import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/*
 * Shared truncation scheduler
 * ---------------------------
 * Pages with many <product-title> instances (a collection grid has 40+)
 * each owned an independent ResizeObserver that called
 * `#calculateTruncation()` synchronously on every resize event burst —
 * including the initial layout pass after page load. Each calculation
 * reads `clientHeight` + `getComputedStyle()` (both force a sync layout
 * if styles have been invalidated) and then writes 5 style properties on
 * the text element (re-invalidating styles). 40+ instances back-to-back
 * = the 152 ms forced-reflow chunk we measured in the production trace.
 *
 * Fix: batch all queued truncation callbacks into ONE animation frame.
 * Within a single frame, the layout cache is computed once and reused
 * across reads — turning O(N) forced layouts into O(1). Wall-clock is
 * dramatically lower, and the work runs off the main task path so
 * filter-click and scroll input stay responsive.
 */
const TRUNCATION_QUEUE = new Set();
let truncationRafScheduled = false;

/**
 * @param {() => void} fn
 */
function scheduleTruncation(fn) {
  TRUNCATION_QUEUE.add(fn);
  if (truncationRafScheduled) return;
  truncationRafScheduled = true;
  requestAnimationFrame(() => {
    // Drain the queue. All reads happen in a single layout pass; subsequent
    // writes are batched by the browser before the next paint. This is the
    // key win — pre-fix, each truncation ran in its own observer tick and
    // forced its own sync layout.
    TRUNCATION_QUEUE.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        console.error('product-title truncation failed:', e);
      }
    });
    TRUNCATION_QUEUE.clear();
    truncationRafScheduled = false;
  });
}

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        // Batch via the shared scheduler — see TRUNCATION_QUEUE comment at
        // the top of this file. Pre-fix, each ResizeObserver callback fired
        // its own sync `#calculateTruncation` and produced cascading forced
        // layouts on collection pages with 40+ titles.
        scheduleTruncation(() => this.#calculateTruncation());
      });

      this.resizeObserver.observe(this);
      scheduleTruncation(() => this.#calculateTruncation());
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize.bind(this));
      scheduleTruncation(() => this.#calculateTruncation());
    }
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle window resize events
   */
  #handleResize() {
    // Same batched-scheduler routing as the ResizeObserver path above —
    // avoids per-resize cascading sync layouts on legacy browsers.
    scheduleTruncation(() => this.#calculateTruncation());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
